import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { adminAPI } from '../../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Tag, Edit2, Trash2, X, Check, Search, Plus, AlertTriangle } from 'lucide-react';

function TagManager() {
  var navigate = useNavigate();
  var [tags, setTags] = useState([]);
  var [loading, setLoading] = useState(true);
  var [searchQuery, setSearchQuery] = useState('');
  
  // Edit mode state
  var [editingTag, setEditingTag] = useState(null);
  var [editValue, setEditValue] = useState('');
  
  // Delete confirmation state
  var [deletingTag, setDeletingTag] = useState(null);
  
  // New tag state
  var [showNewTag, setShowNewTag] = useState(false);
  var [newTagValue, setNewTagValue] = useState('');
  
  // Processing states
  var [processing, setProcessing] = useState(false);

  var fetchTags = useCallback(function() {
    setLoading(true);
    adminAPI.listTagsDetailed()
      .then(function(res) {
        setTags(res.data || []);
      })
      .catch(function(err) {
        console.error('Failed to load tags:', err);
        toast.error('Failed to load tags');
      })
      .finally(function() {
        setLoading(false);
      });
  }, []);

  useEffect(function() {
    fetchTags();
  }, [fetchTags]);

  // Filter tags by search
  var filteredTags = tags.filter(function(tag) {
    if (!searchQuery) return true;
    return tag.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Start editing a tag
  function startEdit(tag) {
    setEditingTag(tag.name);
    setEditValue(tag.name);
    setDeletingTag(null);
  }

  // Cancel editing
  function cancelEdit() {
    setEditingTag(null);
    setEditValue('');
  }

  // Save tag rename
  function saveEdit() {
    if (!editValue.trim() || editValue.trim() === editingTag) {
      cancelEdit();
      return;
    }
    
    setProcessing(true);
    adminAPI.renameTag(editingTag, editValue.trim())
      .then(function(res) {
        toast.success(res.data.message);
        fetchTags();
        cancelEdit();
      })
      .catch(function(err) {
        toast.error('Failed to rename tag');
      })
      .finally(function() {
        setProcessing(false);
      });
  }

  // Confirm delete
  function confirmDelete(tagName) {
    setDeletingTag(tagName);
    setEditingTag(null);
  }

  // Execute delete
  function executeDelete() {
    if (!deletingTag) return;
    
    setProcessing(true);
    adminAPI.deleteTag(deletingTag)
      .then(function(res) {
        toast.success(res.data.message);
        fetchTags();
        setDeletingTag(null);
      })
      .catch(function(err) {
        toast.error('Failed to delete tag');
      })
      .finally(function() {
        setProcessing(false);
      });
  }

  // Create new tag (we'll add it to a sample meal)
  function createNewTag() {
    if (!newTagValue.trim()) {
      setShowNewTag(false);
      return;
    }
    
    // For now, just show that the tag will be created when applied to meals
    toast.success('Tag "' + newTagValue.trim() + '" ready to use. Apply it to meals to save.');
    setNewTagValue('');
    setShowNewTag(false);
  }

  return (
    <div className="min-h-screen bg-background header-safe-top px-6 pb-6">
      {/* Header */}
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button
            onClick={function() { navigate('/admin/meals'); }}
            variant="ghost"
            size="icon"
            className="rounded-full"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-heading font-bold text-2xl">Tag Manager</h1>
            <p className="text-sm text-muted-foreground">
              {tags.length} tags across {tags.reduce(function(sum, t) { return sum + t.total_count; }, 0)} meals
            </p>
          </div>
        </div>

        {/* Search + Add */}
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder="Search tags..."
              value={searchQuery}
              onChange={function(e) { setSearchQuery(e.target.value); }}
              className="pl-10"
              data-testid="search-tags-input"
            />
          </div>
          <Button
            onClick={function() { setShowNewTag(true); }}
            className="bg-accent hover:bg-accent/90"
            data-testid="add-tag-btn"
          >
            <Plus size={18} className="mr-1" />
            New Tag
          </Button>
        </div>

        {/* New Tag Input */}
        {showNewTag && (
          <div className="bg-secondary/30 rounded-xl p-4 mb-4 flex items-center gap-3">
            <Tag size={18} className="text-accent" />
            <Input
              value={newTagValue}
              onChange={function(e) { setNewTagValue(e.target.value); }}
              placeholder="Enter new tag name..."
              className="flex-1"
              autoFocus
              data-testid="new-tag-input"
              onKeyDown={function(e) {
                if (e.key === 'Enter') createNewTag();
                if (e.key === 'Escape') setShowNewTag(false);
              }}
            />
            <Button onClick={createNewTag} size="sm" className="bg-accent">
              <Check size={16} />
            </Button>
            <Button onClick={function() { setShowNewTag(false); setNewTagValue(''); }} size="sm" variant="ghost">
              <X size={16} />
            </Button>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deletingTag && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-background rounded-2xl w-full max-w-sm p-6 shadow-xl">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                  <AlertTriangle size={24} className="text-red-500" />
                </div>
              </div>
              
              <h3 className="font-heading font-bold text-lg text-center mb-2">Delete Tag?</h3>
              <p className="text-sm text-center text-muted-foreground mb-4">
                This will remove <span className="font-semibold text-foreground">"{deletingTag}"</span> from all meals. This cannot be undone.
              </p>
              
              <div className="flex gap-3">
                <Button
                  onClick={function() { setDeletingTag(null); }}
                  variant="outline"
                  className="flex-1"
                  disabled={processing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={executeDelete}
                  className="flex-1 bg-red-500 hover:bg-red-600"
                  disabled={processing}
                  data-testid="confirm-delete-btn"
                >
                  {processing ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  ) : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tags List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredTags.length === 0 ? (
          <div className="text-center py-12">
            <Tag size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">
              {searchQuery ? 'No tags match your search' : 'No tags found'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredTags.map(function(tag) {
              var isEditing = editingTag === tag.name;
              
              return (
                <div 
                  key={tag.name}
                  className="bg-card border border-border/40 rounded-xl p-4 flex items-center gap-4 hover:border-accent/30 transition-all"
                  data-testid={'tag-row-' + tag.name}
                >
                  <Tag size={18} className="text-accent flex-shrink-0" />
                  
                  {isEditing ? (
                    <Input
                      value={editValue}
                      onChange={function(e) { setEditValue(e.target.value); }}
                      className="flex-1"
                      autoFocus
                      data-testid="edit-tag-input"
                      onKeyDown={function(e) {
                        if (e.key === 'Enter') saveEdit();
                        if (e.key === 'Escape') cancelEdit();
                      }}
                    />
                  ) : (
                    <div className="flex-1">
                      <span className="font-medium">{tag.name}</span>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {tag.admin_count > 0 && (
                          <span className="mr-3">{tag.admin_count} admin meal{tag.admin_count !== 1 ? 's' : ''}</span>
                        )}
                        {tag.legacy_count > 0 && (
                          <span>{tag.legacy_count} legacy meal{tag.legacy_count !== 1 ? 's' : ''}</span>
                        )}
                        {tag.total_count === 0 && (
                          <span className="text-amber-500">Unused</span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-1">
                    {isEditing ? (
                      <>
                        <Button
                          onClick={saveEdit}
                          size="sm"
                          className="h-8 w-8 p-0 bg-accent"
                          disabled={processing}
                          data-testid="save-edit-btn"
                        >
                          <Check size={16} />
                        </Button>
                        <Button
                          onClick={cancelEdit}
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          data-testid="cancel-edit-btn"
                        >
                          <X size={16} />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          onClick={function() { startEdit(tag); }}
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          data-testid={'edit-tag-' + tag.name}
                        >
                          <Edit2 size={16} />
                        </Button>
                        <Button
                          onClick={function() { confirmDelete(tag.name); }}
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          data-testid={'delete-tag-' + tag.name}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Bulk Apply Section Info */}
        <div className="mt-8 p-4 bg-secondary/30 rounded-xl">
          <h3 className="font-semibold mb-2">Bulk Tag Operations</h3>
          <p className="text-sm text-muted-foreground">
            To add or remove tags from multiple meals at once, go to the{' '}
            <button 
              onClick={function() { navigate('/admin/meals'); }}
              className="text-accent hover:underline"
            >
              Meal List
            </button>
            , select meals using the checkbox mode, then use "Add to Group" or the tag actions.
          </p>
        </div>
      </div>
    </div>
  );
}

export default TagManager;
